/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
import { FactoryProvider, Inject, Injectable, InjectionToken, Optional, SkipSelf } from '@angular/core';
import { DateTime } from 'luxon';

import { DateAdapter } from './core/datetime';
import { DATE_RANGE_SELECTION_MAX_LENGTH_OWNER, DateRangeSelectionMaxLengthOwner } from './date-range-selection-max-length-owner.token';
import { DateRange } from './date-selection-model';

/** Injection token used to customize the date range selection behavior. */
export const MAT_DATE_RANGE_SELECTION_STRATEGY = new InjectionToken<MatDateRangeSelectionStrategy<any>>(
  'MAT_DATE_RANGE_SELECTION_STRATEGY'
);

/** Object that can be provided in order to customize the date range selection behavior. */
export interface MatDateRangeSelectionStrategy<D> {
  /**
   * Called when the user has finished selecting a value.
   * @param date Date that was selected. Will be null if the user cleared the selection.
   * @param currentRange Range that is currently show in the calendar.
   * @param event DOM event that triggered the selection. Currently only corresponds to a `click`
   *    event, but it may get expanded in the future.
   */
  selectionFinished(date: D | null, currentRange: DateRange<D>, event: Event): DateRange<D>;

  /**
   * Called when the user has activated a new date (e.g. by hovering over
   * it or moving focus) and the calendar tries to display a date range.
   *
   * @param activeDate Date that the user has activated. Will be null if the user moved
   *    focus to an element that's no a calendar cell.
   * @param currentRange Range that is currently shown in the calendar.
   * @param event DOM event that caused the preview to be changed. Will be either a
   *    `mouseenter`/`mouseleave` or `focus`/`blur` depending on how the user is navigating.
   */
  createPreview(activeDate: D | null, currentRange: DateRange<D>, event: Event): DateRange<D>;
}

/** Provides the default date range selection behavior. */
@Injectable()
export class DefaultMatCalendarRangeStrategy<D> implements MatDateRangeSelectionStrategy<D> {
  constructor(
    private _dateAdapter: DateAdapter<D>,
    @Optional()
    @Inject(DATE_RANGE_SELECTION_MAX_LENGTH_OWNER)
    private readonly _dateRangeSelectionMaxLengthOwner: DateRangeSelectionMaxLengthOwner
  ) {}

  selectionFinished(date: D, currentRange: DateRange<D>) {
    let { start, end } = currentRange;

    if (start == null) {
      start = date;
      this.setTemporaryMinMaxValuesIfNeeded(start);
    } else if (
      end == null &&
      date &&
      this._dateAdapter.compareDate(date, start) >= 0 &&
      this.doDatesSatisfyMaxLengthConstraint(date, start)
    ) {
      end = date;
      this.setTemporaryMinMaxValuesIfNeeded();
    } else {
      start = date;
      end = null;
      this.setTemporaryMinMaxValuesIfNeeded(start);
    }

    return new DateRange<D>(start, end);
  }

  createPreview(activeDate: D | null, currentRange: DateRange<D>) {
    let start: D | null = null;
    let end: D | null = null;

    if (currentRange.start && !currentRange.end && activeDate && this.doDatesSatisfyMaxLengthConstraint(activeDate, currentRange.start)) {
      start = currentRange.start;
      end = activeDate;
    }

    return new DateRange<D>(start, end);
  }

  private doDatesSatisfyMaxLengthConstraint(leftDate: D, rightDate: D): boolean {
    if (typeof this._dateRangeSelectionMaxLengthOwner.maxLengthInYears === 'number') {
      const difference = this.createDateFromD(leftDate).diff(this.createDateFromD(rightDate), 'years').years;
      return difference <= this._dateRangeSelectionMaxLengthOwner.maxLengthInYears;
    }

    if (typeof this._dateRangeSelectionMaxLengthOwner.maxLengthInDays === 'number') {
      const difference = this.createDateFromD(leftDate).diff(this.createDateFromD(rightDate), 'days').days;
      return difference <= this._dateRangeSelectionMaxLengthOwner.maxLengthInDays;
    }

    return true;
  }

  private createDateFromD(d: D): DateTime {
    return DateTime.fromISO(
      `${this._dateAdapter.getYear(d)}-${(this._dateAdapter.getMonth(d) + 1).toString().padStart(2, '0')}-${this._dateAdapter
        .getDate(d)
        .toString()
        .padStart(2, '0')}`
    );
  }

  private setTemporaryMinMaxValuesIfNeeded(start?: D): void {
    if (typeof this._dateRangeSelectionMaxLengthOwner.maxLengthInYears === 'number') {
      const minDate = start && this.createDateFromD(start);
      const maxDate =
        start && this.createDateFromD(start).plus({ years: this._dateRangeSelectionMaxLengthOwner.maxLengthInYears, days: -1 });

      this._dateRangeSelectionMaxLengthOwner.writeTemporaryMinMaxValues(minDate, maxDate);
    } else if (typeof this._dateRangeSelectionMaxLengthOwner.maxLengthInDays === 'number') {
      const minDate = start && this.createDateFromD(start);
      const maxDate =
        start && this.createDateFromD(start).plus({ days: Math.max(this._dateRangeSelectionMaxLengthOwner.maxLengthInDays - 1, 1) });

      this._dateRangeSelectionMaxLengthOwner.writeTemporaryMinMaxValues(minDate, maxDate);
    }
  }
}

/** @docs-private */
export function MAT_CALENDAR_RANGE_STRATEGY_PROVIDER_FACTORY(
  parent: MatDateRangeSelectionStrategy<unknown>,
  adapter: DateAdapter<unknown>,
  dateRangeSelectionMaxLengthOwner: DateRangeSelectionMaxLengthOwner
) {
  return parent || new DefaultMatCalendarRangeStrategy(adapter, dateRangeSelectionMaxLengthOwner);
}

/** @docs-private */
export const MAT_CALENDAR_RANGE_STRATEGY_PROVIDER: FactoryProvider = {
  provide: MAT_DATE_RANGE_SELECTION_STRATEGY,
  deps: [
    [new Optional(), new SkipSelf(), MAT_DATE_RANGE_SELECTION_STRATEGY],
    DateAdapter,
    [new Optional(), DATE_RANGE_SELECTION_MAX_LENGTH_OWNER],
  ],
  useFactory: MAT_CALENDAR_RANGE_STRATEGY_PROVIDER_FACTORY,
};
